import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, generateID } from '@domain-group/javascript-utils'

import useProviderDeepLink, { useProviderDeepLinkForm } from '@hooks/useProviderDeepLink'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ManageDeepLink from './components/ManageDeepLink'

const MODAL_KEY = 'CreateOrEditProviderDeepLinkModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateProvider: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditProviderDeepLinkModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateProvider } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { provider, providerDeepLink } = modalPayload

  const providerDeepLinkPayload = useProviderDeepLink(providerDeepLink, provider)
  const {
    callbacks: {
      updateProviderDeepLinks,
    },
    deepLinkStatusKeys,
    updating,
  } = providerDeepLinkPayload

  const formPayload = useProviderDeepLinkForm(providerDeepLink)
  const { entityState, saveEnabled } = formPayload

  const updateProviderPayload = {
    callbacks: {
      action: updateProviderDeepLinks,
      afterAction: closeModal,
    },
    entityParams: { providerDeepLink: { ...entityState, id: providerDeepLink?.id || generateID()  } },
    toastText: `Deep Link ${providerDeepLink?.id ? 'Updated' : 'Created'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn: () => updateProviderDeepLinks({ providerDeepLink, mode: 'delete' }),
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Deep Link',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={providerDeepLink?.id ? 'Edit' : 'Create'}
        titleSecondLine="Deep Link"
      />

      <SidebarModal.Content>
        <ManageDeepLink
          deepLinkStatusKeys={deepLinkStatusKeys}
          formPayload={formPayload}
        />

        {providerDeepLink?.id && (
          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              // @ts-expect-error TS2345
              onClick={() => confirmDelete(confirmDeletePayload)}
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              size="medium"
            >
              Delete Deep Link
            </Button>
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          disabled={!saveEnabled}
          // @ts-expect-error TS2345
          onClick={() => updateProvider(updateProviderPayload)}
          size="large"
        >
          {providerDeepLink?.id ? 'Edit' : 'Add'} Deep Link
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderDeepLinkModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
